import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';
import { SiteFooter } from '../components/landing/SiteFooter';

const CONTACT_EMAIL = 'hello@theroomgroups.com';

const Shell = ({ title, updated, children, testId }) => (
    <div className="min-h-screen bg-background flex flex-col" data-testid={testId}>
        <header className="border-b border-border/50">
            <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
                <Link to="/" aria-label="The Room home"><BrandLogo /></Link>
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="legal-back-home">
                    <ArrowLeft className="w-4 h-4" /> Home
                </Link>
            </div>
        </header>
        <main className="flex-grow max-w-3xl w-full mx-auto px-6 py-12 space-y-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{title}</h1>
            {updated && <p className="text-sm text-muted-foreground">Last updated: {updated}</p>}
            <div className="space-y-5 text-base leading-relaxed text-foreground/90 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-8 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1">
                {children}
            </div>
        </main>
        <SiteFooter />
    </div>
);

export const PrivacyPage = () => (
    <Shell title="Privacy Policy" updated="October 2026" testId="privacy-page">
        <p>This policy explains what information The Room collects, how it is used, and which outside services help us run the app.</p>
        <h2>Information we collect</h2>
        <ul>
            <li>Account details: your name, email address and an encrypted password.</li>
            <li>Group activity: lesson progress, attendance, discussion replies, chat and direct messages.</li>
            <li>Files your leaders upload, such as lesson resources and recordings.</li>
        </ul>
        <h2>How we use it</h2>
        <p>We use this information only to run your group: showing lessons, tracking progress and sending notifications or emails you have asked for. We do not sell personal data.</p>
        <h2>Who can see your data</h2>
        <p>Content you share is visible to members and leaders of your own group. Group leaders can see member progress and attendance.</p>
        <h2>Services that handle data</h2>
        <p>We rely on these providers to run The Room. Each only receives the data it needs:</p>
        <ul>
            <li><strong>Emergent</strong>: hosting, database and file storage for accounts, lessons, uploads and recordings.</li>
            <li><strong>PostHog</strong>: visitor analytics, such as pages viewed and device type, to help us improve the app.</li>
            <li><strong>Zoom</strong>: live meetings and cloud recordings when your leader uses Zoom.</li>
            <li><strong>YouTube</strong>: lesson videos embedded from YouTube. YouTube may set its own cookies when a video plays.</li>
            <li><strong>Daily.co</strong>: in-app live video rooms.</li>
            <li><strong>Resend</strong>: sending emails such as invites and password resets.</li>
        </ul>
        <h2>Your choices</h2>
        <p>You can ask your group leader or contact us to update or delete your account and data.</p>
        <h2>Contact</h2>
        <p>Questions? Email <a className="text-primary underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
    </Shell>
);

export const TermsPage = () => (
    <Shell title="Terms of Service" updated="October 2026" testId="terms-page">
        <p>By using The Room you agree to these terms.</p>
        <h2>Using The Room</h2>
        <ul>
            <li>Keep your login details private and accurate.</li>
            <li>Be respectful. Leaders may hide messages or remove members who break group guidelines.</li>
            <li>Only upload content you have the right to share.</li>
        </ul>
        <h2>Group leaders</h2>
        <p>Leaders are responsible for the members they approve and the content they publish to their group.</p>
        <h2>Early access</h2>
        <p>The Room is offered free during early access. Features may change, and we will give notice before introducing any paid plans.</p>
        <h2>Liability</h2>
        <p>The service is provided as is. We work to keep it available and secure but cannot guarantee uninterrupted access.</p>
        <h2>Contact</h2>
        <p>Email <a className="text-primary underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with any questions.</p>
    </Shell>
);

export const ContactPage = () => (
    <Shell title="Contact us" testId="contact-page">
        <p>We would love to hear from pastors, group leaders and members.</p>
        <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 h-12 font-semibold hover:bg-primary/90 transition-colors"
            data-testid="contact-email-link"
        >
            <Mail className="w-4 h-4" /> {CONTACT_EMAIL}
        </a>
        <p className="text-muted-foreground">We usually reply within two working days.</p>
    </Shell>
);
